/**
 * Analytics Event Types
 * Centralized event definitions for PostHog tracking
 */

// Event categories for organization
export const enum EventCategory {
  AUTH = "auth",
  PROFILE = "profile",
  JOBS = "jobs",
  ENRICHMENT = "enrichment",
  SETTINGS = "settings",
  SOCIAL = "social",
  API = "api",
  ERROR = "error",
}

// Frontend Events
export const enum FrontendEvent {
  // Auth events
  LOGIN_ATTEMPTED = "login_attempted",
  LOGIN_SUCCESS = "login_success",
  LOGIN_FAILED = "login_failed",
  LOGOUT = "logout",
  SIGNUP_STARTED = "signup_started",
  SIGNUP_COMPLETED = "signup_completed",

  // Onboarding events
  CR_SIGNUP = "cr_signup",
  CR_INFO_GATHERING = "cr_info_gathering",
  CR_SOCIALMEDIA_MANUAL = "cr_socialmedia_manual",
  CR_NICHE_MANUAL = "cr_niche_manual",
  CR_PROFILE_BUILDER = "cr_profile_builder",
  CR_PROFILE_APPEAL = "cr_profile_appeal",
  CR_ONBOARDING_WHATSAPP = "cr_onboarding_whatsapp",
  CR_ONBOARDING_SHAREJOB = "cr_onboarding_sharejob",
  CR_ONBOARDING_POSTSTUDIO = "cr_onboarding_poststudio",
  CR_ONBOARDING_BIOLINK = "cr_onboarding_biolink",
  CR_ONBOARDING_COMPLETE = "cr_onboarding_complete",

  // Creator Dashboard & Navigation events
  CR_DASHBOARD = "cr_dashboard",
  CR_LOGOUT = "cr_logout",

  // Creator Profile Sharing events
  CR_FIRST_PROFILE_SHARE = "cr_first_profile_share",
  CR_PROFILE_SHARE = "cr_profile_share",
  CR_SHARE_PROFILE_COPYLINK = "cr_share_profile_copylink",
  CR_SHARE_PROFILE_REDIRECTION = "cr_share_profile_redirection",
  CR_SEO_EDIT = "cr_seo_edit",
  CR_SHARE_PROFILE_EDIT = "cr_share_profile_edit",

  // Creator Job Sharing events
  CR_SHARE_FIRST_JOB = "cr_share_first_job",
  CR_SHARE_JOB = "cr_share_job",
  CR_FIRST_JOB_APPLICATION = "cr_first_job_application",

  // Creator Account Management events
  CR_DELETE_ACCOUNT = "cr_delete_account",
  CR_LINK_ACCOUNT = "cr_link_account",

  // My Link - Profile Editing events
  CR_EDIT_PROFILE = "cr_edit_profile",
  CR_EDIT_PROFILE_PIC = "cr_edit_profile_pic",
  CR_EDIT_PROFILE_NAME = "cr_edit_profile_name",
  CR_EDIT_PROFILE_LINK = "cr_edit_profile_link",
  CR_EDIT_PROFILE_BIO = "cr_edit_profile_bio",
  CR_EDIT_PROFILE_NICHE = "cr_edit_profile_niche",
  CR_EDIT_PROFILE_SOCIALLINK = "cr_edit_profile_sociallink",

  // My Link - Design events
  CR_DESIGN_START = "cr_design_start",
  CR_DESIGN_EDIT = "cr_design_edit",

  // My Link - Settings events
  CR_SETTINGS = "cr_settings",
  CR_SETTINGS_NICHE_ADD = "cr_settings_niche_add",
  CR_SETTINGS_NICHE_REMOVE = "cr_settings_niche_remove",
  CR_SETTINGS_JOBKW_ADD = "cr_settings_jobkw_add",
  CR_SETTINGS_JOBKW_REMOVE = "cr_settings_jobkw_remove",
  CR_SETTINGS_COUNTRY_ADD = "cr_settings_country_add",
  CR_SETTINGS_COUNTRY_REMOVE = "cr_settings_country_remove",
  CR_SETTINGS_EMPIND_ADD = "cr_settings_empind_add",
  CR_SETTINGS_EMPIND_REMOVE = "cr_settings_empind_remove",
  CR_SETTINGS_EMPKW_ADD = "cr_settings_empkw_add",
  CR_SETTINGS_EMPKW_REMOVE = "cr_settings_empkw_remove",

  // My Link - Filter events
  CR_FILTER = "cr_filter",
  CR_FILTER_JOBS = "cr_filter_jobs",
  CR_FILTER_COMPANY = "cr_filter_company",

  // Earnings & Payout events
  CR_EARNING = "cr_earning",
  CR_PAYOUT_DOWNLOAD = "cr_payout_download",
  CR_PAYOUT_ADDED = "cr_payout_added",
  CR_FIRST_REVENUE = "cr_first_revenue",
  CR_REVENUE_UPDATE = "cr_revenue_update",

  // Profile events
  PROFILE_EDIT_STARTED = "profile_edit_started",
  PROFILE_UPDATED = "profile_updated",
  AVATAR_UPLOADED = "avatar_uploaded",
  BIO_UPDATED = "bio_updated",

  // Job events
  JOB_CARD_VIEWED = "job_card_viewed",
  JOB_CARD_CLICKED = "job_card_clicked",
  JOB_SAVED = "job_saved",
  JOB_UNSAVED = "job_unsaved",
  JOB_APPLIED = "job_applied",
  JOB_SHARED = "job_shared",

  // Search & Filter events
  SEARCH_PERFORMED = "search_performed",
  FILTER_APPLIED = "filter_applied",
  FILTER_CLEARED = "filter_cleared",
  AUTOCOMPLETE_USED = "autocomplete_used",

  // Settings events
  SETTINGS_OPENED = "settings_opened",
  PREFERENCES_UPDATED = "preferences_updated",
  NOTIFICATION_SETTINGS_CHANGED = "notification_settings_changed",
  THEME_CHANGED = "theme_changed",

  // Social events
  SOCIAL_ACCOUNT_CONNECTED = "social_account_connected",
  SOCIAL_ACCOUNT_DISCONNECTED = "social_account_disconnected",
  LINKEDIN_SYNC_INITIATED = "linkedin_sync_initiated",

  // Navigation events
  PAGE_VIEWED = "page_viewed",
  BOTTOM_SHEET_OPENED = "bottom_sheet_opened",
  BOTTOM_SHEET_CLOSED = "bottom_sheet_closed",
  MODAL_OPENED = "modal_opened",
  MODAL_CLOSED = "modal_closed",

  // Engagement events
  BUTTON_CLICKED = "button_clicked",
  LINK_CLICKED = "link_clicked",
  FEATURE_DISCOVERED = "feature_discovered",

  // Audience events
  AUD_SOCIALMEDIA_CLICK = "AUD_SOCIALMEDIA_CLICK",
  AUD_JOB_APPLY = "AUD_JOB_APPLY",
  AUD_JOB_SEARCH = "AUD_JOB_SEARCH",
  AUD_JOB_FILTER_JOB = "AUD_JOB_FILTER_JOB",
  AUD_EARN_CLICK = "AUD_EARN_CLICK",
  AUD_VPN_RESTRICTION_SHOWN = "AUD_VPN_RESTRICTION_SHOWN",
  AUD_COUNTRY_RESTRICTION_SHOWN = "AUD_COUNTRY_RESTRICTION_SHOWN",
  AUD_INCOGNITO_RESTRICTION_SHOWN = "AUD_INCOGNITO_RESTRICTION_SHOWN",
  AUD_JOB_ALERT_EMAIL_SUBMITTED = "AUD_JOB_ALERT_EMAIL_SUBMITTED",
  AUD_JOB_ALERT_PREFS_SUBMITTED = "AUD_JOB_ALERT_PREFS_SUBMITTED",
  AUD_JOB_ALERT_UNSUBSCRIBED = "AUD_JOB_ALERT_UNSUBSCRIBED",
  AUD_RESUME_UPLOAD = "AUD_RESUME_UPLOAD",
  AUD_RESUME_VERIFICATION = "AUD_RESUME_VERIFICATION",
  AUD_RESUME_LOGIN = "AUD_RESUME_LOGIN",
  AUD_RESUME_LOGOUT = "AUD_RESUME_LOGOUT",
  // Post Studio events
  CR_POSTSTUDIO = "cr_poststudio",
  CR_POSTSTUDIO_JOBSSELECT = "cr_poststudio_jobsselect",
  CR_POSTSTUDIO_PLATFORM = "cr_poststudio_platform",
  CR_POSTSTUDIO_REGENERATE = "cr_poststudio_regenerate",
  CR_POSTSTUDIO_SHARE = "cr_poststudio_share",
  CR_RECAP_DOWNLOAD = "cr_recap_download",
  CR_RECAP_SHARE = "cr_recap_share",
  CR_REFER_AND_EARN = "CR_REFER&EARN",
  CR_REFER_AND_EARN_COPYLINK = "CR_REFER&EARN_COPYLINK",
  CR_REFER_AND_EARN_SHARE = "CR_REFER&EARN_SHARE",
  CR_REFER_AND_EARN_WHATSAPPINVITE = "CR_REFER&EARN_WHATSAPPINVITE",
  CR_REFER_AND_EARN_RESOURCES_PLAYBOOK = "CR_REFER&EARN_RESOURCES_PLAYBOOK",
  CR_REFER_AND_EARN_RESOURCES_IDEA = "CR_REFER&EARN_RESOURCES_IDEA",
  CR_REFER_AND_EARN_RESOURCES_VIDEO = "CR_REFER&EARN_RESOURCES_VIDEO",
  REFER_AND_EARN_INVITATION = "REFER&EARN_INVITATION",
  CR_EARN_MODAL_CLICK = "cr_earn_modal_click",
  CR_EARN_LEADERBOARD_CLICK = "cr_earn_leaderboard_click",
}

// Backend Events
export const enum BackendEvent {
  // Auth events
  CREATOR_REGISTERED = "creator_registered",
  CREATOR_LOGIN = "creator_login",
  TOKEN_REFRESHED = "token_refreshed",
  PASSWORD_RESET_REQUESTED = "password_reset_requested",
  PASSWORD_RESET_COMPLETED = "password_reset_completed",

  // Onboarding events (backend tracking)
  CR_SIGNUP_BACKEND = "cr_signup_backend",
  CR_INFO_GATHERING_BACKEND = "cr_info_gathering_backend",
  CR_PROFILE_BUILDER_BACKEND = "cr_profile_builder_backend",

  // Creator Job Application events
  CR_FIRST_JOB_APPLICATION = "cr_first_job_application",

  // Profile events
  PROFILE_CREATED = "profile_created",
  PROFILE_ENRICHMENT_STARTED = "profile_enrichment_started",
  PROFILE_ENRICHMENT_COMPLETED = "profile_enrichment_completed",
  PROFILE_ENRICHMENT_FAILED = "profile_enrichment_failed",
  PROFILE_APPROVED = "profile_approved",
  PROFILE_REJECTED = "profile_rejected",

  // Social sync events
  LINKEDIN_SYNC_STARTED = "linkedin_sync_started",
  LINKEDIN_SYNC_COMPLETED = "linkedin_sync_completed",
  LINKEDIN_SYNC_FAILED = "linkedin_sync_failed",
  SOCIAL_ACCOUNT_ADDED = "social_account_added",
  SOCIAL_ACCOUNT_REMOVED = "social_account_removed",

  // Job events
  JOB_FEED_FETCHED = "job_feed_fetched",
  JOB_FILTERS_FETCHED = "job_filters_fetched",
  JOB_SEARCH_PERFORMED = "job_search_performed",
  SIMILAR_JOBS_FETCHED = "similar_jobs_fetched",

  // API events
  API_REQUEST = "api_request",
  API_ERROR = "api_error",
  RATE_LIMIT_HIT = "rate_limit_hit",
  EXTERNAL_API_CALLED = "external_api_called",

  // Analytics events
  DASHBOARD_DATA_FETCHED = "dashboard_data_fetched",
  ANALYTICS_VIEWED = "analytics_viewed",

  // Revenue Milestones
  REVENUE_ACTIVATION_AMOUNT = "REVENUE_ACTIVATION_AMOUNT",
  REVENUE_ACTIVATION_5 = "REVENUE_ACTIVATION_5",
  REVENUE_ACTIVATION_10 = "REVENUE_ACTIVATION_10",
  REVENUE_ACTIVATION_20 = "REVENUE_ACTIVATION_20",
  REVENUE_ACTIVATION_50 = "REVENUE_ACTIVATION_50",
  REVENUE_ACTIVATION_100 = "REVENUE_ACTIVATION_100",

  // Error events
  VALIDATION_ERROR = "validation_error",
  DATABASE_ERROR = "database_error",
  EXTERNAL_SERVICE_ERROR = "external_service_error",
  UNHANDLED_ERROR = "unhandled_error",
}

// Event property types for type safety
export interface BaseEventProperties {
  timestamp?: string;
  sessionId?: string;
  userAgent?: string;
  platform?: string;
  $set?: Record<string, any>;
  $set_once?: Record<string, any>;
  [key: string]: unknown;
}

export interface AuthEventProperties extends BaseEventProperties {
  method?: "email" | "social" | "magic-link";
  provider?: string;
  errorReason?: string;
}

export interface ProfileEventProperties extends BaseEventProperties {
  profileId?: string;
  username?: string;
  nicheCategories?: string[];
  primaryPlatform?: string;
  totalFollowers?: number;
  enrichmentStatus?: string;
}

export interface PageViewEventProperties extends BaseEventProperties {
  creator_id?: string;
  creator_username?: string;
  page_type?: string;
  page_name?: string;
}

export interface JobEventProperties extends BaseEventProperties {
  jobId?: string;
  jobTitle?: string;
  company?: string;
  category?: string;
  workMode?: string;
  location?: string;
  salary?: number;
  source?: string;
}

export interface SearchEventProperties extends BaseEventProperties {
  query?: string;
  filters?: Record<string, any>;
  resultsCount?: number;
  searchType?: "keyword" | "autocomplete" | "filter";
}

export interface AudienceSocialMediaClickProperties extends BaseEventProperties {
  social_media: string;
  username?: string;
}

export interface AudienceJobEventProperties extends BaseEventProperties {
  job_id: string;
  job_title: string;
  job_company: string;
  job_value?: number;
  revenue_type?: "RPA" | "RPC";
  jobs_viewed_in_session?: number;
  username?: string;
}

export interface AudienceJobSearchProperties extends BaseEventProperties {
  job_search: string;
  username?: string;
}

export interface AudienceJobFilterProperties extends BaseEventProperties {
  filters_changed: string[];
  filters_applied_count: number;
  username?: string;
}

export interface AudienceEarnClickProperties extends BaseEventProperties {
  ref_username: string;
  ref_username_with_at: string;
  username?: string;
}

export interface AudienceVpnRestrictionProperties extends BaseEventProperties {
  job_id?: string;
  browser_timezone: string;
  ip_timezone: string;
  username?: string;
  creator_id?: string;
  creator_username?: string;
}

export interface AudienceCountryRestrictionProperties extends BaseEventProperties {
  job_id: string;
  job_country: string;
  seeker_country: string;
  username?: string;
  creator_id?: string;
  creator_username?: string;
}

export interface AudienceIncognitoRestrictionProperties extends BaseEventProperties {
  job_id?: string;
  job_title?: string;
  job_company?: string;
  username?: string;
  creator_id?: string;
  creator_username?: string;
}

export interface AudienceJobAlertEmailSubmittedProperties extends BaseEventProperties {
  email: string;
  username?: string;
}

export interface AudienceJobAlertPrefsSubmittedProperties extends BaseEventProperties {
  email: string;
  frequency: string;
  experience: string;
  username?: string;
}

export interface AudienceJobAlertUnsubscribedProperties extends BaseEventProperties {
  creator_username: string;
  subscription_external_id: string;
  remaining_creator_alerts?: number;
  username?: string;
}

export interface ApiEventProperties extends BaseEventProperties {
  endpoint?: string;
  method?: string;
  statusCode?: number;
  responseTime?: number;
  errorMessage?: string;
  errorCode?: string;
}

export interface ErrorEventProperties extends BaseEventProperties {
  errorType?: string;
  errorMessage?: string;
  errorStack?: string;
  endpoint?: string;
  userId?: string;
}

// Onboarding event properties
export interface OnboardingSignupProperties extends BaseEventProperties {
  social_media: string; // linkedin, instagram, twitter, etc.
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface OnboardingInfoGatheringProperties extends BaseEventProperties {
  creator_profile?: "SUCCESS" | "FAIL";
  creator_niche?: "SUCCESS" | "FAIL";
  creator_jobs?: "SUCCESS" | "FAIL";
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface OnboardingSocialMediaManualProperties extends BaseEventProperties {
  social_media: string;
  social_media_handle?: string;
  social_media_url?: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface OnboardingNicheManualProperties extends BaseEventProperties {
  primary_niche: string;
  secondary_niche?: string[];
  keywords?: string[];
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface OnboardingProfileBuilderProperties extends BaseEventProperties {
  status: "approved" | "rejected" | "waitlist";
  artha_link?: string;
  primary_niche?: string;
  secondary_niche?: string[];
  keywords?: string[];
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface OnboardingProfileAppealProperties extends BaseEventProperties {
  rejection_reason?: string;
  appeal_message?: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Dashboard event properties
export interface DashboardEventProperties extends BaseEventProperties {
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Logout event properties
export interface LogoutEventProperties extends BaseEventProperties {
  session_duration?: number;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Profile share type
export type ShareType = "linked" | "other";
export type SourceScreen = "main_share" | "my_link" | "linked_account";

// Creator Profile Share event properties
export interface ProfileShareEventProperties extends BaseEventProperties {
  share_type: ShareType;
  source_screen: SourceScreen;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Copy Link event properties
export interface CopyLinkEventProperties extends BaseEventProperties {
  source_screen: SourceScreen;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Profile Redirection event properties
export interface ProfileRedirectionEventProperties extends BaseEventProperties {
  linked_account: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator SEO Edit event properties
export interface SeoEditEventProperties extends BaseEventProperties {
  settings?: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Share Profile Edit event properties
export interface ShareProfileEditEventProperties extends BaseEventProperties {
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Job Share event properties
export interface JobShareEventProperties extends BaseEventProperties {
  social_media: string;
  job_id: string;
  job_title: string;
  job_company: string;
  job_cpa_value?: number;
  job_cpc_value?: number;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Delete Account event properties
export interface DeleteAccountEventProperties extends BaseEventProperties {
  reason?: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// Creator Link Account event properties
export interface LinkAccountEventProperties extends BaseEventProperties {
  social_media: string;
  source_screen: SourceScreen;
  linked_accounts: string[];
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// My Link - Profile Edit event properties
export interface EditProfileEventProperties extends BaseEventProperties {
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface EditProfileNameEventProperties extends BaseEventProperties {
  name: string;
  username?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface EditProfileNicheEventProperties extends BaseEventProperties {
  primary_niche?: string;
  secondary_niche?: string[];
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface EditProfileSocialLinkEventProperties extends BaseEventProperties {
  social_media: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface EditProfileLinkEventProperties extends BaseEventProperties {
  username: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// My Link - Design event properties
export type DesignChangeType = "Pagebg" | "profile" | "title" | "cta" | "pagetxt";

export interface DesignEditEventProperties extends BaseEventProperties {
  design_change: DesignChangeType;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface EarningEventProperties extends BaseEventProperties {
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface PayoutDownloadEventProperties extends BaseEventProperties {
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface PayoutAddedEventProperties extends BaseEventProperties {
  bank_connected?: boolean;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface RevenueEventProperties extends BaseEventProperties {
  lifetime_revenue?: number;
  monthly_revenue?: number;
  weekly_revenue?: number;
  revenue_currency?: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface PostStudioEventProperties extends BaseEventProperties {
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface PostStudioPlatformEventProperties extends PostStudioEventProperties {
  post_channel: string;
}

// My Link - Settings event properties
export interface SettingsNicheEventProperties extends BaseEventProperties {
  primary_niche?: string;
  secondary_niche?: string[];
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface SettingsKeywordEventProperties extends BaseEventProperties {
  keywords: string[];
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface SettingsCountryEventProperties extends BaseEventProperties {
  job_country: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface SettingsIndustryEventProperties extends BaseEventProperties {
  industry?: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

// My Link - Filter event properties
export interface FilterJobsEventProperties extends BaseEventProperties {
  filters_changed: string[];
  filters_applied_count: number;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface FilterCompanyEventProperties extends BaseEventProperties {
  filters_changed: string[];
  filters_applied_count: number;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

export interface RecapDownloadEventProperties extends BaseEventProperties {
  month: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
}

export interface RecapShareEventProperties extends BaseEventProperties {
  social_media_platform: string;
  month: string;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
}

// Creator First Job Application event properties (Backend)
export interface FirstJobApplicationEventProperties extends BaseEventProperties {
  job_id: string;
  job_title: string;
  job_company: string;
  job_cpa_value?: number;
  job_cpc_value?: number;
  jobs_viewed_in_session?: number;
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}


// Engagement tiers based on follower count
export type EngagementTier = "nano" | "micro" | "macro" | "mega";

// Helper type for all events
export type AnalyticsEvent = FrontendEvent | BackendEvent;

// Helper type for all event properties
export type EventProperties =
  | AuthEventProperties
  | ProfileEventProperties
  | PageViewEventProperties
  | JobEventProperties
  | SearchEventProperties
  | AudienceSocialMediaClickProperties
  | AudienceJobEventProperties
  | AudienceJobSearchProperties
  | AudienceJobFilterProperties
  | AudienceEarnClickProperties
  | ApiEventProperties
  | ErrorEventProperties
  | BaseEventProperties
  | DashboardEventProperties
  | LogoutEventProperties
  | ProfileShareEventProperties
  | CopyLinkEventProperties
  | ProfileRedirectionEventProperties
  | SeoEditEventProperties
  | ShareProfileEditEventProperties
  | JobShareEventProperties
  | DeleteAccountEventProperties
  | LinkAccountEventProperties
  | FirstJobApplicationEventProperties
  | EditProfileEventProperties
  | EditProfileNameEventProperties
  | EditProfileNicheEventProperties
  | EditProfileSocialLinkEventProperties
  | EditProfileLinkEventProperties
  | DesignEditEventProperties
  | EarningEventProperties
  | PayoutDownloadEventProperties
  | PayoutAddedEventProperties
  | RevenueEventProperties
  | SettingsNicheEventProperties
  | SettingsKeywordEventProperties
  | SettingsCountryEventProperties
  | SettingsIndustryEventProperties
  | FilterJobsEventProperties
  | FilterCompanyEventProperties
  | PostStudioEventProperties
  | PostStudioPlatformEventProperties
  | AudienceVpnRestrictionProperties
  | AudienceCountryRestrictionProperties
  | AudienceIncognitoRestrictionProperties
  | AudienceJobAlertEmailSubmittedProperties
  | AudienceJobAlertPrefsSubmittedProperties
  | AudienceJobAlertUnsubscribedProperties
  | RecapDownloadEventProperties
  | RecapShareEventProperties;

// ============================================
// Multi-Provider Analytics System
// ============================================

/**
 * Analytics Provider Interface
 * All analytics providers must implement this interface
 */
export interface AnalyticsProvider {
  readonly name: string;
  readonly enabled: boolean;

  // Core tracking methods
  identify(userId: string, properties?: Record<string, unknown>): Promise<void>;
  track(eventName: string, properties?: Record<string, unknown>): Promise<void>;
  page(pageName?: string, properties?: Record<string, unknown>): Promise<void>;
  alias(newId: string, previousId?: string): Promise<void>;
  reset(): Promise<void>;

  // Provider lifecycle
  initialize?(): Promise<void>;
  destroy?(): Promise<void>;
}

/**
 * Analytics Configuration
 */
export interface AnalyticsConfig {
  posthog: {
    enabled: boolean;
    apiKey: string;
    host?: string;
  };
  customerio: {
    enabled: boolean;
    siteId: string;
    apiKey: string;
  };
  // Easy to extend with new providers
  amplitude?: {
    enabled: boolean;
    apiKey: string;
  };
  mixpanel?: {
    enabled: boolean;
    token: string;
  };
}

/**
 * Analytics Provider Error
 */
export class AnalyticsProviderError extends Error {
  constructor(
    public readonly providerName: string,
    message: string,
    public readonly originalError?: Error
  ) {
    super(`[${providerName}] ${message}`);
    this.name = 'AnalyticsProviderError';
  }
}
